import {
  Drawer,
  VStack,
  HStack,
  Text,
  Grid,
  Box,
  Heading,
  Separator,
  IconButton,
  Card,
  Tabs,
  Badge
} from '@chakra-ui/react'
import { useTranslation } from 'react-i18next'
import {
  X,
  Trash2,
  ChevronUp,
  ChevronDown,
  Home,
  Users,
  Calendar,
  User,
  Clock,
  CalendarClock,
  FolderOpen,
} from 'lucide-react'
import {
  useProperty,
  useDeleteProperty,
  useUpdateProperty,
  usePropertyManager,
  usePropertyStats,
} from '../hooks/useProperties'
import { useAuth } from '@/features/auth/hooks/useAuth'
import { LoadingState } from '@/shared/components/LoadingState'
import { ErrorState } from '@/shared/components/ErrorState'
import { EditableField } from '@/shared/components/EditableField'

interface PropertyDetailDrawerProps {
  propertyId: string | null
  isOpen: boolean
  onClose: () => void
  onNavigatePrevious?: () => void
  onNavigateNext?: () => void
  hasPrevious?: boolean
  hasNext?: boolean
}

export function PropertyDetailDrawer({
  propertyId,
  isOpen,
  onClose,
  onNavigatePrevious,
  onNavigateNext,
  hasPrevious = false,
  hasNext = false,
}: PropertyDetailDrawerProps) {
  const { t } = useTranslation()
  const { data: property, isLoading, error } = useProperty(propertyId ?? undefined)
  const { data: managerName } = usePropertyManager(propertyId ?? undefined)
  const { data: stats } = usePropertyStats(propertyId ?? undefined)
  const { data: auth } = useAuth()
  const deleteProperty = useDeleteProperty()
  const updateProperty = useUpdateProperty()

  // Check if user can delete this property
  // Only ADMIN and MANAGER can delete residences
  const canDelete = () => {
    if (!propertyId || !auth) return false

    // ADMIN can delete all residences
    if (auth.isAdmin) return true

    // MANAGER can delete their managed residences
    return auth.isManager(propertyId)
  }

  const handleDelete = async () => {
    if (!propertyId) return
    if (!window.confirm(t('properties.delete_confirm'))) return

    try {
      await deleteProperty.mutateAsync(propertyId)
      onClose()
    } catch (error) {
      console.error('Error deleting property:', error)
      alert(t('common.error'))
    }
  }

  const handleFieldUpdate = async (fieldName: string, value: string | number) => {
    if (!propertyId) return

    try {
      await updateProperty.mutateAsync({
        id: propertyId,
        [fieldName]: value || null,
      })
    } catch (error) {
      console.error('Error updating property:', error)
      alert(t('common.error'))
    }
  }

  const handleInteractOutside = (e: { detail: { originalEvent: { target: EventTarget | null } } }) => {
    const target = e.detail.originalEvent.target as HTMLElement

    // Chercher la ligne du tableau cliquée (uniquement les lignes de données, pas le header)
    const tableRow = target.closest('[data-property-row]') as HTMLElement
    const propertyCard = target.closest('[data-property-card]') as HTMLElement

    if (tableRow) {
      // Récupérer l'ID de la propriété depuis la ligne
      // La ligne a un onClick qui sera déclenché, donc on ne fait rien ici
      // Le handlePropertyClick parent sera appelé automatiquement
      return
    }

    if (propertyCard) {
      // Même chose pour les cards
      return
    }

    // Clic vraiment en dehors (ni tableau ni cards) → Fermer le drawer
    onClose()
  }

  return (
    <Drawer.Root
      open={isOpen}
      placement="end"
      size="lg"
      modal={false}
      onInteractOutside={handleInteractOutside}
    >
      <Drawer.Positioner pointerEvents="none">
        <Drawer.Content maxW="800px" pointerEvents="auto">
          <Drawer.Header borderBottomWidth="1px" py={4}>
            <Box display="flex" alignItems="center" w="full" gap={3}>
              {/* Navigation arrows - Left */}
              <Box display="flex" gap={1} flexShrink={0}>
                <IconButton
                  variant="ghost"
                  size="sm"
                  onClick={onNavigatePrevious}
                  disabled={!hasPrevious}
                  aria-label={t('common.previous')}
                  title={t('common.previous')}
                >
                  <ChevronUp size={18} />
                </IconButton>
                <IconButton
                  variant="ghost"
                  size="sm"
                  onClick={onNavigateNext}
                  disabled={!hasNext}
                  aria-label={t('common.next')}
                  title={t('common.next')}
                >
                  <ChevronDown size={18} />
                </IconButton>
              </Box>

              {/* Title - Center */}
              <Box flex={1} minW={0}>
                {property && (
                  <>
                    <Text fontSize="xs" color="gray.500" fontWeight="medium" lineHeight="1.3">
                      {property.residence_key || property.id.slice(0, 8)}
                    </Text>
                    <Heading
                      size="md"
                      lineHeight="1.3"
                      overflow="hidden"
                      textOverflow="ellipsis"
                      whiteSpace="nowrap"
                    >
                      {property.name}
                    </Heading>
                    {managerName && (
                      <HStack gap={1.5} mt={1}>
                        <Badge
                          size="sm"
                          variant="subtle"
                          colorPalette="purple"
                          display="flex"
                          alignItems="center"
                          gap={1}
                        >
                          <User size={10} />
                          <Text fontSize="xs">{managerName}</Text>
                        </Badge>
                      </HStack>
                    )}
                  </>
                )}
                {!property && (
                  <Heading size="md">{t('properties.details')}</Heading>
                )}
              </Box>

              {/* Actions menu and close - Right */}
              <Box display="flex" gap={2} flexShrink={0}>
                {property && canDelete() && (
                  <IconButton
                    variant="ghost"
                    size="sm"
                    colorPalette="red"
                    onClick={handleDelete}
                    aria-label={t('common.delete')}
                    title={t('common.delete')}
                  >
                    <Trash2 size={18} />
                  </IconButton>
                )}
                <IconButton variant="ghost" size="sm" onClick={onClose} aria-label={t('common.cancel')}>
                  <X size={18} />
                </IconButton>
              </Box>
            </Box>
          </Drawer.Header>

          <Drawer.Body p={0} display="flex" flexDirection="column" height="100%">
            {isLoading && <LoadingState message={t('common.loading')} />}

            {error && <ErrorState message={t('errors.not_found')} />}

            {property && propertyId && (
              <VStack align="stretch" gap={4} flex={1} height="100%">
                {/* Timestamps */}
                <HStack
                  gap={3}
                  px={6}
                  pt={4}
                  pb={3}
                  bg="gray.50"
                  borderBottomWidth="1px"
                  borderColor="gray.200"
                  flexWrap="wrap"
                >
                  <HStack gap={2} flex={1} minW="200px">
                    <Box color="blue.500">
                      <CalendarClock size={16} />
                    </Box>
                    <VStack align="flex-start" gap={0}>
                      <Text fontSize="xs" color="gray.600" fontWeight="medium">
                        {t('common.created_on')}
                      </Text>
                      <Text fontSize="sm" fontWeight="semibold" color="gray.800">
                        {new Date(property.created_at ?? '').toLocaleDateString('fr-FR', {
                          day: 'numeric',
                          month: 'long',
                          year: 'numeric',
                        })}
                      </Text>
                    </VStack>
                  </HStack>

                  {property.updated_at && property.updated_at !== property.created_at && (
                    <HStack gap={2} flex={1} minW="200px">
                      <Box color="green.500">
                        <Clock size={16} />
                      </Box>
                      <VStack align="flex-start" gap={0}>
                        <Text fontSize="xs" color="gray.600" fontWeight="medium">
                          {t('common.last_updated')}
                        </Text>
                        <Text fontSize="sm" fontWeight="semibold" color="gray.800">
                          {new Date(property.updated_at).toLocaleDateString('fr-FR', {
                            day: 'numeric',
                            month: 'long',
                            year: 'numeric',
                          })}
                        </Text>
                      </VStack>
                    </HStack>
                  )}
                </HStack>

                {/* Stats Cards */}
                <Box px={6} pt={4}>
                  <Grid templateColumns={{ base: '1fr', md: 'repeat(2, 1fr)', lg: 'repeat(4, 1fr)' }} gap={3}>
                    <Card.Root
                      size="sm"
                      variant="outline"
                      cursor="pointer"
                      _hover={{ borderColor: 'brand.500', bg: 'brand.50' }}
                    >
                      <Card.Body>
                        <VStack align="flex-start" gap={1}>
                          <HStack gap={2}>
                            <Home size={16} />
                            <Text fontSize="xs" color="gray.600">
                              Lots
                            </Text>
                          </HStack>
                          <Text fontSize="2xl" fontWeight="bold">
                            {stats?.unitsCount ?? 0}
                          </Text>
                          <Text fontSize="xs" color="gray.500">
                            {(stats?.totalShares ?? 0).toLocaleString('fr-FR')} tantièmes
                          </Text>
                        </VStack>
                      </Card.Body>
                    </Card.Root>

                    <Card.Root
                      size="sm"
                      variant="outline"
                      cursor="pointer"
                      _hover={{ borderColor: 'brand.500', bg: 'brand.50' }}
                    >
                      <Card.Body>
                        <VStack align="flex-start" gap={1}>
                          <HStack gap={2}>
                            <Users size={16} />
                            <Text fontSize="xs" color="gray.600">
                              Copropriétaires
                            </Text>
                          </HStack>
                          <Text fontSize="2xl" fontWeight="bold">
                            {stats?.ownersCount ?? 0}
                          </Text>
                          <Text fontSize="xs" color="gray.500">
                            actifs
                          </Text>
                        </VStack>
                      </Card.Body>
                    </Card.Root>

                    <Card.Root
                      size="sm"
                      variant="outline"
                      cursor="pointer"
                      _hover={{ borderColor: 'brand.500', bg: 'brand.50' }}
                    >
                      <Card.Body>
                        <VStack align="flex-start" gap={1}>
                          <HStack gap={2}>
                            <FolderOpen size={16} />
                            <Text fontSize="xs" color="gray.600">
                              GED
                            </Text>
                          </HStack>
                          <Text fontSize="2xl" fontWeight="bold">
                            247
                          </Text>
                          <Text fontSize="xs" color="gray.500">
                            fichiers
                          </Text>
                        </VStack>
                      </Card.Body>
                    </Card.Root>

                    <Card.Root size="sm" variant="outline">
                      <Card.Body>
                        <VStack align="flex-start" gap={1}>
                          <HStack gap={2}>
                            <Calendar size={16} />
                            <Text fontSize="xs" color="gray.600">
                              Prochaine AG
                            </Text>
                          </HStack>
                          <Text fontSize="sm" fontWeight="bold">
                            15 Déc 2024
                          </Text>
                          <Text fontSize="xs" color="gray.500">
                            14h30
                          </Text>
                        </VStack>
                      </Card.Body>
                    </Card.Root>
                  </Grid>
                </Box>

                {/* Tabs avec contenu scrollable */}
                <Box flex={1} minHeight={0} px={6} pb={6}>
                  <Tabs.Root defaultValue="general" height="100%" display="flex" flexDirection="column">
                    <Tabs.List>
                      <Tabs.Trigger value="general">Informations générales</Tabs.Trigger>
                      <Tabs.Trigger value="cs">Conseil Syndical</Tabs.Trigger>
                      <Tabs.Trigger value="charges">Charges</Tabs.Trigger>
                      <Tabs.Trigger value="rules">Règles</Tabs.Trigger>
                      <Tabs.Trigger value="equipment">Équipements</Tabs.Trigger>
                    </Tabs.List>

                    <Box flex={1} minHeight={0} overflowY="auto" mt={4}>
                      <Tabs.Content value="general">
                        <VStack align="stretch" gap={6}>
                          {/* Card Identification de la résidence */}
                          <Card.Root variant="outline">
                            <Card.Header>
                              <Heading size="sm">Identification</Heading>
                            </Card.Header>
                            <Card.Body>
                              <Grid templateColumns={{ base: '1fr', md: 'repeat(2, 1fr)' }} gap={4}>
                                <EditableField
                                  label={t('properties.fields.name')}
                                  value={property.name}
                                  fieldName="name"
                                  onUpdate={handleFieldUpdate}
                                />
                                <EditableField
                                  label={t('properties.fields.key')}
                                  value={property.residence_key}
                                  fieldName="residence_key"
                                  placeholder="Non renseigné"
                                  onUpdate={handleFieldUpdate}
                                />
                              </Grid>
                            </Card.Body>
                          </Card.Root>

                          {/* Card Localisation */}
                          <Card.Root variant="outline">
                            <Card.Header>
                              <Heading size="sm">{t('properties.sections.location')}</Heading>
                            </Card.Header>
                            <Card.Body>
                              <Grid templateColumns={{ base: '1fr', md: 'repeat(2, 1fr)' }} gap={4}>
                                <EditableField
                                  label={t('properties.fields.address')}
                                  value={property.address}
                                  fieldName="address"
                                  onUpdate={handleFieldUpdate}
                                />
                                <EditableField
                                  label={t('properties.fields.city')}
                                  value={property.city}
                                  fieldName="city"
                                  onUpdate={handleFieldUpdate}
                                />
                                <EditableField
                                  label={t('properties.fields.postal_code')}
                                  value={property.postal_code}
                                  fieldName="postal_code"
                                  onUpdate={handleFieldUpdate}
                                />
                                <EditableField
                                  label={t('properties.fields.country')}
                                  value={property.country}
                                  fieldName="country"
                                  onUpdate={handleFieldUpdate}
                                />
                              </Grid>
                            </Card.Body>
                          </Card.Root>

                          <Separator />

                          {/* Informations légales */}
                          <Box>
                            <Heading size="sm" mb={4}>
                              {t('properties.sections.legal_info')}
                            </Heading>
                            <Grid templateColumns="1fr" gap={4}>
                              <EditableField
                                label={t('properties.fields.legal_representative')}
                                value={property.legal_representative}
                                fieldName="legal_representative"
                                onUpdate={handleFieldUpdate}
                              />
                              <EditableField
                                label={t('properties.fields.siret')}
                                value={property.siret}
                                fieldName="siret"
                                onUpdate={handleFieldUpdate}
                              />
                            </Grid>
                          </Box>

                          <Separator />

                          {/* Autres informations */}
                          <Box>
                            <Heading size="sm" mb={4}>
                              {t('properties.sections.other_info')}
                            </Heading>
                            <Grid templateColumns="1fr" gap={4}>
                              <EditableField
                                label={t('properties.fields.total_shares')}
                                value={property.total_shares?.toString()}
                                fieldName="total_shares"
                                placeholder="Non renseigné"
                                onUpdate={handleFieldUpdate}
                              />
                            </Grid>
                          </Box>
                        </VStack>
                      </Tabs.Content>

                      <Tabs.Content value="cs">
                        <Text color="gray.500">Contenu Conseil Syndical à venir...</Text>
                      </Tabs.Content>

                      <Tabs.Content value="charges">
                        <Text color="gray.500">Contenu Charges à venir...</Text>
                      </Tabs.Content>

                      <Tabs.Content value="rules">
                        <Text color="gray.500">Contenu Règles à venir...</Text>
                      </Tabs.Content>

                      <Tabs.Content value="equipment">
                        <Text color="gray.500">Contenu Équipements à venir...</Text>
                      </Tabs.Content>
                    </Box>
                  </Tabs.Root>
                </Box>
              </VStack>
            )}
          </Drawer.Body>

        </Drawer.Content>
      </Drawer.Positioner>
    </Drawer.Root>
  )
}
